"use client";

import { subscribeToRecipeBoxInvitations } from "@/lib/recipe-box-invitations-service";
import { subscribeToRecipeBox, subscribeToUserRecipeBoxes } from "@/lib/recipe-box-service";
import { subscribeToRecipes } from "@/lib/recipe-service";
import type { Recipe, RecipeBox, RecipeBoxInvitation } from "@/lib/types";
import type { FirestoreError } from "firebase/firestore";
import { useEffect, useState } from "react";

type RecipeBoxesState = {
  boxes: RecipeBox[];
  loading: boolean;
  error: string | null;
};

export function useRecipeBoxes(ownerUid: string | null | undefined) {
  const [state, setState] = useState<RecipeBoxesState>({ boxes: [], loading: true, error: null });

  useEffect(() => {
    if (!ownerUid) {
      return undefined;
    }

     
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const unsubscribe = subscribeToUserRecipeBoxes(
      ownerUid,
      (boxes) => {
        setState({ boxes, loading: false, error: null });
      },
      (error: FirestoreError) => {
        console.error("Failed to load recipe boxes", error);
        setState({ boxes: [], loading: false, error: "Failed to load recipe boxes." });
      },
    );

    return unsubscribe;
  }, [ownerUid]);

  if (!ownerUid) {
    return { boxes: [], loading: false, error: null };
  }

  return state;
}

export { createRecipeBox, updateRecipeBox } from "@/lib/recipe-box-service";

type RecipeBoxState = {
  box: RecipeBox | null;
  loading: boolean;
  error: string | null;
};

export function useRecipeBox(recipeBoxId: string | null | undefined) {
  const [state, setState] = useState<RecipeBoxState>({ box: null, loading: true, error: null });

  useEffect(() => {
    if (!recipeBoxId) {
      return undefined;
    }

     
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const unsubscribe = subscribeToRecipeBox(
      recipeBoxId,
      (box) => {
        setState({ box, loading: false, error: null });
      },
      (error: FirestoreError) => {
        console.error("Failed to load recipe box", error);
        setState({ box: null, loading: false, error: "Failed to load recipe box." });
      },
    );

    return unsubscribe;
  }, [recipeBoxId]);

  if (!recipeBoxId) {
    return { box: null, loading: false, error: null };
  }

  return state;
}

type RecipesState = {
  recipes: Recipe[];
  loading: boolean;
  error: string | null;
};

export function useRecipes(recipeBoxId: string | null | undefined) {
  const [state, setState] = useState<RecipesState>({ recipes: [], loading: true, error: null });

  useEffect(() => {
    if (!recipeBoxId) {
      return undefined;
    }

     
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const unsubscribe = subscribeToRecipes(
      recipeBoxId,
      (recipes) => {
        setState({ recipes, loading: false, error: null });
      },
      (error: FirestoreError) => {
        console.error("Failed to load recipes", error);
        setState({ recipes: [], loading: false, error: "Failed to load recipes." });
      },
    );

    return unsubscribe;
  }, [recipeBoxId]);

  if (!recipeBoxId) {
    return { recipes: [], loading: false, error: null };
  }

  return state;
}

type InvitationsState = {
  invitations: RecipeBoxInvitation[];
  loading: boolean;
  error: string | null;
};

export function useRecipeBoxInvitations(recipeBoxId: string | null | undefined) {
  const [state, setState] = useState<InvitationsState>({ invitations: [], loading: true, error: null });

  useEffect(() => {
    if (!recipeBoxId) {
      return undefined;
    }

     
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const unsubscribe = subscribeToRecipeBoxInvitations(
      recipeBoxId,
      (invitations) => {
        setState({ invitations, loading: false, error: null });
      },
      (error) => {
        console.error("Failed to load recipe box invitations", error);
        setState({ invitations: [], loading: false, error: "Failed to load invitations." });
      },
    );

    return unsubscribe;
  }, [recipeBoxId]);

  if (!recipeBoxId) {
    return { invitations: [], loading: false, error: null };
  }

  return state;
}
